import { useEffect, useState } from "react";
import { DownloadSimple, Lightning } from "@phosphor-icons/react";
import useSEO from "@/hooks/useSEO";
import { PageHeader } from "@/components/PageHeader";
import { API, BACKEND_URL } from "@/lib/site";
import useT from "@/i18n";
import { formatBytes, formatDate, releasedAt } from "@/lib/format";
import { breadcrumbLd, webPageLd } from "@/lib/seo";

export default function Changelog() {
  const [releases, setReleases] = useState(null);
  const { t: T, lp } = useT();
  const t = T.changelog;
  const path = "/changelog";
  const title = t.seoTitle;
  const description = t.seoDesc;
  const crumbs = [{ name: t.crumb, path }];
  useSEO({ title, description, path, jsonLd: [webPageLd({ title, description, path: lp(path) }), breadcrumbLd(crumbs, T.crumbs.home, lp)] });

  useEffect(() => {
    fetch(`${API}/apk/releases?t=${Date.now()}`, { cache: "no-store" })
      .then((r) => r.json())
      .then((d) => setReleases(d.releases || []))
      .catch(() => setReleases([]));
  }, []);

  return (
    <div data-testid="changelog-page">
      <PageHeader crumbs={crumbs} badge={t.badge} title={t.title} intro={t.intro} />
      <section className="max-w-4xl mx-auto safe-x sm:px-8 pb-16" aria-label={t.aria}>
        {releases === null && <p className="text-slate-400">{t.loading}</p>}
        {releases?.length === 0 && <p className="text-slate-400" data-testid="changelog-empty">{t.empty}</p>}
        <ol className="relative border-l pl-6 sm:pl-8 space-y-8" style={{ borderColor: "rgba(0,191,255,.2)" }}>
          {releases?.map((r) => (
            <li key={r.id} className="relative" data-testid={`changelog-release-${r.version_code}`}>
              <span className={`absolute -left-[31px] sm:-left-[39px] top-6 w-3 h-3 rounded-full ${r.is_live ? "bg-[#00BFFF] pulse-ring" : "bg-slate-600"}`} aria-hidden="true" />
              <article className="glass rounded-2xl p-5 sm:p-7 min-w-0">
                <header className="flex flex-wrap items-center gap-3">
                  <h2 className="font-display text-2xl font-bold">v{r.version_name}</h2>
                  <span className="font-mono-alt text-xs text-slate-500">#{r.version_code}</span>
                  {r.is_live ? (
                    <span className="px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-[#00BFFF]/15 text-[#7DDCFF] border border-[#00BFFF]/30">{t.live}</span>
                  ) : (
                    <span className="px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-white/5 text-slate-400 border border-white/10">{t.archived}</span>
                  )}
                  {r.force_update && <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-red-500/15 text-red-300 border border-red-400/30"><Lightning size={10} weight="fill" aria-hidden="true" />{t.force}</span>}
                </header>
                <p className="mt-2 text-xs text-slate-500">
                  <time dateTime={releasedAt(r)}>{formatDate(releasedAt(r), T.dateLocale)}</time> · {formatBytes(r.size)} · Android {r.min_android}+
                </p>
                <p className="mt-4 text-sm text-slate-300 whitespace-pre-wrap leading-relaxed break-words">{r.release_notes || t.noNotes}</p>
                <div className="mt-4 font-mono-alt text-[11px] text-slate-500 break-all">SHA-256: {r.sha256}</div>
                {r.is_live && (
                  <a href={`${BACKEND_URL}${r.download_url}`} className="btn-primary mt-5 text-sm" data-testid={`changelog-download-${r.version_code}`} download>
                    <DownloadSimple size={18} weight="bold" aria-hidden="true" /> {t.dl(r.version_name)}
                  </a>
                )}
              </article>
            </li>
          ))}
        </ol>
      </section>
    </div>
  );
}
